import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Check,
    Trophy,
    Undo2,
    ChevronDown,
    ChevronUp,
    MessageSquare,
    Send,
} from "lucide-react";
import type { Task } from "@/types";
import { formatTime } from "@/utils/helpers";
import { TaskComments } from "@/components/TaskComments";
import { TagDisplay } from "@/components/TagInput";

interface CompletedTaskItemProps {
    task: Task;
    onUndo: () => void;
    showUndo: boolean;
    onEditComment?: (commentId: string, newText: string) => void;
    onDeleteComment?: (commentId: string) => void;
    onAddComment?: (text: string) => void;
}

export function CompletedTaskItem({
    task,
    onUndo,
    showUndo,
    onEditComment,
    onDeleteComment,
    onAddComment,
}: CompletedTaskItemProps) {
    const [showDetails, setShowDetails] = useState(false);
    const [showAddComment, setShowAddComment] = useState(false);
    const [newComment, setNewComment] = useState("");
    const remainingSeconds = task.estimatedMinutes * 60 - task.elapsedSeconds;
    const wasOvertime = remainingSeconds < 0;
    const hasDetails =
        (task.comments && task.comments.length > 0) ||
        (task.tags && task.tags.length > 0) ||
        !!onAddComment;

    const handleAddComment = () => {
        if (newComment.trim() && onAddComment) {
            onAddComment(newComment.trim());
            setNewComment("");
            setShowAddComment(false);
        }
    };

    return (
        <div className="group p-4 rounded-xl bg-gradient-to-r from-green-500/5 to-emerald-500/5 border border-green-500/20">
            <div className="flex items-center gap-4">
                <div className="shrink-0">
                    <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                        <Check className="w-5 h-5 text-green-600" />
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-medium break-words flex items-center gap-2">
                        {task.name}
                        <Trophy className="w-4 h-4 text-yellow-500 shrink-0" />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5 flex-wrap">
                        <span>{formatTime(task.elapsedSeconds)} spent</span>
                        {wasOvertime && (
                            <Badge
                                variant="destructive"
                                className="text-xs h-5"
                            >
                                +{formatTime(Math.abs(remainingSeconds))}
                            </Badge>
                        )}
                        {task.tags && task.tags.length > 0 && (
                            <TagDisplay tags={task.tags} size="sm" />
                        )}
                        {hasDetails && (
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-5 px-1 text-xs"
                                onClick={() => setShowDetails(!showDetails)}
                            >
                                {showDetails ? (
                                    <ChevronUp className="w-3 h-3" />
                                ) : (
                                    <ChevronDown className="w-3 h-3" />
                                )}
                            </Button>
                        )}
                    </div>
                </div>
                {showUndo && (
                    <Button
                        size="icon"
                        variant="ghost"
                        onClick={onUndo}
                        title="Undo completion"
                        className="h-8 w-8 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0"
                    >
                        <Undo2 className="w-4 h-4" />
                    </Button>
                )}
            </div>

            {/* Expanded Details Section */}
            {showDetails && hasDetails && (
                <div className="mt-4 pt-4 border-t border-green-500/20 space-y-3">
                    {/* Tags */}
                    {task.tags && task.tags.length > 0 && (
                        <TagDisplay tags={task.tags} />
                    )}

                    {/* Comments Section */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                Notes
                            </span>
                            {onAddComment && !showAddComment && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-5 text-xs px-1"
                                    onClick={() => setShowAddComment(true)}
                                >
                                    + Add
                                </Button>
                            )}
                        </div>

                        {showAddComment && onAddComment && (
                            <div className="flex gap-2 mb-2">
                                <Input
                                    placeholder="Add a retrospective note..."
                                    value={newComment}
                                    onChange={(e) =>
                                        setNewComment(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter")
                                            handleAddComment();
                                        if (e.key === "Escape") {
                                            setShowAddComment(false);
                                            setNewComment("");
                                        }
                                    }}
                                    className="h-8 text-sm"
                                    autoFocus
                                />
                                <Button
                                    size="icon"
                                    onClick={handleAddComment}
                                    disabled={!newComment.trim()}
                                    className="h-8 w-8"
                                >
                                    <Send className="w-3 h-3" />
                                </Button>
                            </div>
                        )}

                        {task.comments &&
                        task.comments.length > 0 &&
                        onEditComment &&
                        onDeleteComment ? (
                            <TaskComments
                                comments={task.comments}
                                onEdit={onEditComment}
                                onDelete={onDeleteComment}
                                maxVisible={5}
                            />
                        ) : (
                            !showAddComment && (
                                <p className="text-xs text-muted-foreground">
                                    No notes yet
                                </p>
                            )
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
